import { onBeforeUnmount, ref, watch, type Ref } from 'vue'
import { LocalAudioTrack, RoomEvent, Track, TrackEvent, type Room } from 'livekit-client'
import { liveKitManager } from './manager'
import { isNativeMicAvailable, NativeMicSource } from './nativeMic'

export interface MicLevelMeter {
  level: Ref<number>
  stop: () => void
}

export interface MicLevelMeterOptions {
  /**
   * Outside a call there is no published mic track to read. With this set, the meter opens its
   * own capture of the returned device (`'default'` = the system one) while no room is active and
   * releases it the moment a call starts — for the settings page, where the user tunes
   * sensitivity before joining. Re-captures when the returned id changes.
   */
  previewDeviceId?: () => string | null | undefined
}

/**
 * Live 0..1 mic input level for the local participant's currently published microphone
 * track, sampled via a dedicated Web Audio `AnalyserNode` (independent of `MicGainChain` /
 * RNNoise, which own the actual publish-side audio graph — this only reads from it).
 * Re-attaches whenever the underlying `MediaStreamTrack` changes (device switch, mic restart)
 * or the `Room` itself is replaced (reconnect), and reports 0 while muted, since a disabled
 * `MediaStreamTrack` yields silence.
 *
 * Plain function, not a composable — `watch()` doesn't need an active component instance, so
 * this also works from a long-lived singleton like `debugPublisher` that outlives any one
 * component. `useMicLevelMeter()` below is the thin Vue-lifecycle wrapper for template usage.
 */
export function createMicLevelMeter(options: MicLevelMeterOptions = {}): MicLevelMeter {
  const level = ref(0)

  let audioContext: AudioContext | null = null
  let analyser: AnalyserNode | null = null
  let sourceNode: MediaStreamAudioSourceNode | null = null
  let dataArray: Uint8Array | null = null
  let rafId: number | null = null
  let currentTrackId: string | null = null
  let roomListeners: Array<() => void> = []
  let processorUpdateTrack: LocalAudioTrack | null = null
  let offProcessorUpdate: (() => void) | null = null
  let activeRoom: Room | null = null
  let previewStream: MediaStream | null = null
  let releasePreview: (() => void) | null = null
  let previewPending = false
  // Bumped on every stop, so a getUserMedia that resolves after it is discarded, not kept.
  let previewRequest = 0
  let stopped = false

  function currentMicMediaStreamTrack(room: Room | null): MediaStreamTrack | undefined {
    const publication = room?.localParticipant.getTrackPublication(Track.Source.Microphone)
    const track = publication?.track
    return track instanceof LocalAudioTrack ? track.mediaStreamTrack : undefined
  }

  function sourceTrack(): MediaStreamTrack | undefined {
    return currentMicMediaStreamTrack(activeRoom) ?? previewStream?.getAudioTracks()[0]
  }

  function stopPreview() {
    previewRequest++
    previewPending = false
    releasePreview?.()
    releasePreview = null
    previewStream = null
  }

  /** The preview capture: `getUserMedia`, or on macOS the same native capture calls use (see `nativeMic.ts`). */
  async function acquirePreview(deviceId: string | null | undefined): Promise<{ stream: MediaStream; release: () => void }> {
    if (isNativeMicAvailable()) {
      const context = new AudioContext()
      try {
        if (context.state === 'suspended') await context.resume().catch(() => undefined)
        const source = await NativeMicSource.start(context, deviceId || 'default')
        return {
          stream: new MediaStream([source.track]),
          release: () => {
            void source.stop()
            void context.close().catch(() => undefined)
          },
        }
      } catch (error) {
        void context.close().catch(() => undefined)
        throw error
      }
    }
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: deviceId && deviceId !== 'default' ? { deviceId: { exact: deviceId } } : true,
    })
    return { stream, release: () => stream.getTracks().forEach(track => track.stop()) }
  }

  function startPreview() {
    if (!options.previewDeviceId || activeRoom || previewStream || previewPending || stopped) return
    if (!isNativeMicAvailable() && !navigator.mediaDevices?.getUserMedia) return
    const deviceId = options.previewDeviceId()
    const request = ++previewRequest
    previewPending = true
    acquirePreview(deviceId)
      .then(({ stream, release }) => {
        if (request !== previewRequest) {
          release()
          return
        }
        previewPending = false
        previewStream = stream
        releasePreview = release
        syncTrack(activeRoom)
      })
      .catch((error) => {
        if (request !== previewRequest) return
        previewPending = false
        // No access or no such device — the meter just stays empty, as it did before.
        console.warn('Mic level preview unavailable', error)
      })
  }

  function teardownAnalyser() {
    if (rafId !== null) {
      cancelAnimationFrame(rafId)
      rafId = null
    }
    try { sourceNode?.disconnect() } catch { /* already disconnected */ }
    sourceNode = null
    analyser = null
    dataArray = null
    currentTrackId = null
    level.value = 0
    if (audioContext) {
      const ctx = audioContext
      audioContext = null
      void ctx.close().catch(() => undefined)
    }
  }

  function tick() {
    // Belt-and-suspenders check alongside the room/track event listeners below: LiveKit swaps
    // `mediaStreamTrack` under us for several reasons (processor attach/detach, `restartTrack()`
    // from an echo-cancellation/device change, possibly others not covered by an explicit event
    // in every SDK version), and missing any one of them leaves the analyser silently reading a
    // stale, already-stopped track. Comparing the live track id once per frame is cheap and
    // catches all of them uniformly instead of chasing every individual LiveKit event by name.
    const liveTrack = sourceTrack()
    if (liveTrack?.id !== currentTrackId) {
      syncTrack(activeRoom)
      return
    }
    if (!analyser || !dataArray) return
    analyser.getByteTimeDomainData(dataArray)
    let sumSquares = 0
    for (let i = 0; i < dataArray.length; i++) {
      const normalized = (dataArray[i]! - 128) / 128
      sumSquares += normalized * normalized
    }
    const rms = Math.sqrt(sumSquares / dataArray.length)
    // A normal speaking voice sits well under RMS 0.25 on this scale, so multiply up
    // to make the meter actually use its visual range instead of barely twitching.
    level.value = Math.min(1, rms * 4)
    rafId = requestAnimationFrame(tick)
  }

  function setupForTrack(mediaStreamTrack: MediaStreamTrack) {
    teardownAnalyser()
    audioContext = new AudioContext()
    if (audioContext.state === 'suspended') void audioContext.resume().catch(() => undefined)
    analyser = audioContext.createAnalyser()
    analyser.fftSize = 512
    analyser.smoothingTimeConstant = 0.6
    sourceNode = audioContext.createMediaStreamSource(new MediaStream([mediaStreamTrack]))
    sourceNode.connect(analyser)
    dataArray = new Uint8Array(analyser.frequencyBinCount)
    currentTrackId = mediaStreamTrack.id
    rafId = requestAnimationFrame(tick)
  }

  function detachProcessorUpdateListener() {
    offProcessorUpdate?.()
    processorUpdateTrack = null
    offProcessorUpdate = null
  }

  function syncTrack(room: Room | null) {
    const publication = room?.localParticipant.getTrackPublication(Track.Source.Microphone)
    const track = publication?.track

    // `track.mediaStreamTrack` swaps out from under us whenever a processor (RNNoise) is
    // attached/detached via `setProcessor()`/`stopProcessor()`, or the track itself is
    // reacquired via `restartTrack()` (echo-cancellation toggle, mic device switch) — LiveKit
    // only signals either via a `TrackEvent` fired on the track itself (`TrackProcessorUpdate` /
    // `Restarted`), never as a `RoomEvent`, so the listeners below alone would leave the analyser
    // attached to a now-stopped track (see `AggressiveNoiseProcessor.destroy()` / `LocalTrack.restart()`
    // stopping the old `MediaStreamTrack`) whenever either happens mid-call.
    if (track instanceof LocalAudioTrack && track !== processorUpdateTrack) {
      detachProcessorUpdateListener()
      const handler = () => syncTrack(room)
      track.on(TrackEvent.TrackProcessorUpdate, handler)
      track.on(TrackEvent.Restarted, handler)
      processorUpdateTrack = track
      offProcessorUpdate = () => {
        track.off(TrackEvent.TrackProcessorUpdate, handler)
        track.off(TrackEvent.Restarted, handler)
      }
    } else if (!(track instanceof LocalAudioTrack)) {
      detachProcessorUpdateListener()
    }

    // In a call the published track is the only source — never a second capture next to it.
    if (room) stopPreview()
    const mediaStreamTrack = (track instanceof LocalAudioTrack ? track.mediaStreamTrack : undefined)
      ?? previewStream?.getAudioTracks()[0]
    if (!mediaStreamTrack) {
      teardownAnalyser()
      startPreview()
      return
    }
    if (mediaStreamTrack.id === currentTrackId) return
    setupForTrack(mediaStreamTrack)
  }

  function attachRoomListeners(room: Room | null) {
    for (const off of roomListeners.splice(0)) off()
    if (!room) return
    const handler = () => syncTrack(room)
    const events = [
      RoomEvent.LocalTrackPublished,
      RoomEvent.LocalTrackUnpublished,
      RoomEvent.TrackMuted,
      RoomEvent.TrackUnmuted,
    ] as const
    for (const event of events) room.on(event, handler)
    roomListeners.push(() => {
      for (const event of events) room.off(event, handler)
    })
  }

  const stopWatch = watch(() => liveKitManager.state.room, (room) => {
    activeRoom = room
    attachRoomListeners(room)
    syncTrack(room)
  }, { immediate: true })

  const stopDeviceWatch = options.previewDeviceId
    ? watch(options.previewDeviceId, () => {
        if (!previewStream && !previewPending) return
        stopPreview()
        syncTrack(activeRoom)
      })
    : () => undefined

  function stop() {
    stopped = true
    stopWatch()
    stopDeviceWatch()
    stopPreview()
    activeRoom = null
    for (const off of roomListeners.splice(0)) off()
    detachProcessorUpdateListener()
    teardownAnalyser()
  }

  return { level, stop }
}

/** Vue-lifecycle wrapper around `createMicLevelMeter()` for use directly in a component's `<script setup>`. */
export function useMicLevelMeter(options?: MicLevelMeterOptions) {
  const meter = createMicLevelMeter(options)
  onBeforeUnmount(() => meter.stop())
  return { level: meter.level }
}
